import { useEffect, useState } from "react";
import { useFiscalDefaults } from "../../lib/hooks/useFiscalDefaults";
import { ApiClientError } from "../../lib/api/client";
import { uploadFile } from "../../lib/api/files";
import { useCreateExpenseMutation, useSubmitExpenseMutation } from "../../lib/api/transactions";
import { formatMoney } from "../../lib/format/money";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { FormField, FormGrid } from "../../components/ui/FormField";
import { AllocationLinesEditor } from "./AllocationLinesEditor";
import {
  type AllocationLineForm,
  sumAllocationAmounts,
} from "./transaction-utils";

interface NewExpenseModalProps {
  open: boolean;
  onClose: () => void;
  units: { id: string; name: string; code: string }[];
  funds: { id: string; name: string }[];
  categories: { id: string; name: string }[];
}

export function NewExpenseModal({
  open,
  onClose,
  units,
  funds,
  categories,
}: NewExpenseModalProps) {
  const createExpense = useCreateExpenseMutation();
  const submitExpense = useSubmitExpenseMutation();
  const { defaultDate } = useFiscalDefaults();
  const [payee, setPayee] = useState("");
  const [expenseDate, setExpenseDate] = useState("");
  const [description, setDescription] = useState("");
  const [missingEvidenceReason, setMissingEvidenceReason] = useState("");
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [lines, setLines] = useState<AllocationLineForm[]>([
    {
      unitId: units[0]?.id ?? "",
      fundId: funds[0]?.id ?? "",
      categoryId: categories[0]?.id ?? "",
      amount: "",
      costType: "program_direct",
    },
  ]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && defaultDate) {
      setExpenseDate(defaultDate);
    }
  }, [open, defaultDate]);

  const total = sumAllocationAmounts(lines);

  async function save(submitForApproval: boolean) {
    setError(null);

    const evidenceReason = missingEvidenceReason.trim();
    if (submitForApproval && !evidenceFile && !evidenceReason) {
      setError("Supporting evidence or a missing-evidence reason is required.");
      return;
    }

    try {
      const created = await createExpense.mutateAsync({
        payee,
        expenseDate,
        description: description || undefined,
        missingEvidenceReason: evidenceFile ? undefined : evidenceReason || undefined,
        submit: false,
        lines: lines.map((line) => ({
          unitId: line.unitId,
          fundId: line.fundId,
          categoryId: line.categoryId,
          costType: line.costType ?? "program_direct",
          amount: Number.parseFloat(line.amount).toFixed(2),
        })),
      });

      if (evidenceFile) {
        await uploadFile({
          file: evidenceFile,
          entityType: "expense",
          entityId: created.expense.id,
        });
      }

      if (submitForApproval) {
        await submitExpense.mutateAsync({
          id: created.expense.id,
          missingEvidenceReason: evidenceFile ? undefined : evidenceReason || undefined,
        });
      }

      onClose();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not save expense.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New expense"
      description="Create a draft or submit it for independent approval."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            disabled={createExpense.isPending || submitExpense.isPending}
            onClick={() => void save(false)}
          >
            Save draft
          </Button>
          <Button
            disabled={createExpense.isPending || submitExpense.isPending}
            onClick={() => void save(true)}
          >
            {createExpense.isPending || submitExpense.isPending
              ? "Saving…"
              : "Submit for approval"}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {error ? <ErrorBanner message={error} /> : null}
        <FormGrid>
          <FormField label="Payee / supplier">
            <input
              className="edlab-input"
              value={payee}
              onChange={(e) => {
                setPayee(e.target.value);
              }}
              required
            />
          </FormField>
          <FormField label="Expense date">
            <input
              type="date"
              className="edlab-input"
              value={expenseDate}
              onChange={(e) => {
                setExpenseDate(e.target.value);
              }}
            />
          </FormField>
          <FormField label="Description" fullWidth>
            <input
              className="edlab-input"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
            />
          </FormField>
          <FormField label="Supporting evidence (PDF, JPEG, PNG, WebP)" fullWidth>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png,image/webp"
              className="edlab-input"
              onChange={(e) => {
                setEvidenceFile(e.target.files?.[0] ?? null);
              }}
            />
          </FormField>
          <FormField label="Missing evidence reason (if no file attached)" fullWidth>
            <input
              className="edlab-input"
              value={missingEvidenceReason}
              onChange={(e) => {
                setMissingEvidenceReason(e.target.value);
              }}
              placeholder="Explain why evidence is not attached yet"
            />
          </FormField>
        </FormGrid>
        <AllocationLinesEditor
          lines={lines}
          onChange={setLines}
          units={units}
          funds={funds}
          categories={categories}
          showCostType
        />
        <p className="text-sm font-medium text-edlab-ink">
          Total: {formatMoney(total.toFixed(2))}
        </p>
      </div>
    </Modal>
  );
}
